import type { ReactNode } from "react";
import { countFormula, RANK_BY_LABELS, type RankingOptions } from "../utils/factors";

/** A checkbox's state, shown next to the term it adds. */
const Opt = ({ on }: { on: boolean }) => <span className="xp-opt">{on ? "on" : "off"}</span>;

type TermKey = "weight" | "share" | "found" | "topic" | "review" | "cites" | "age";

/** A term of the formula, tinted the same in the formula and in its definition. */
const T = ({ k, children }: { k: TermKey; children: ReactNode }) => (
  <span className={`xp-term xp-t-${k}`}>{children}</span>
);

const TOPIC = "P(on-topic)";
const REVIEW = "(1 − 0.7 × P(review))";
const CITES = "(1 + ln(1 + citations))²";
const AGE = "0.5^(years since publication ÷ 3)";

/** Greys out a term of the score while people rank by a count, which ignores it. */
const scoreOnly = (o: RankingOptions): string | undefined =>
  o.rank_by === "score" ? undefined : "xp-off";

/** The formula at the top of the FAQ, as the options are checked: a count's, or the score's. */
const LiveFormula = ({ options }: { options: RankingOptions }) => {
  const count = countFormula(options, new Date().getFullYear() - 3);
  if (count)
    return (
      <p className="xp-formula">
        {count}. A paper counts when <T k="topic">{TOPIC}</T> is at least 0.4.
      </p>
    );
  return (
    <>
      <p className="xp-formula">
        Score = Σ (<T k="weight">paper weight</T> × <T k="share">authorship credit</T>), over the
        person&rsquo;s papers
        {options.foundational ? (
          <>
            , <T k="found">foundational papers</T> included
          </>
        ) : (
          " found by search"
        )}
      </p>
      <p className="xp-formula">
        <T k="weight">Paper weight</T> = <T k="topic">{TOPIC}</T> × <T k="review">{REVIEW}</T> ×{" "}
        <T k="cites">{CITES}</T>
        {options.recent ? (
          <>
            {" "}
            × <T k="age">{AGE}</T>
          </>
        ) : null}
      </p>
      <p className="xp-formula">
        <T k="share">Authorship credit</T> ={" "}
        {options.first_authors
          ? "1 for a first or sole author; 0 for middle and last authors"
          : "1 for a first, last or sole author; 0.5 ÷ √m for each of m middle authors"}
      </p>
    </>
  );
};

/** How the ranking works, in the Evidence API page's FAQ style; the formula at the top follows
 * the options as checked. */
export const ExpertsFaq = ({ options }: { options: RankingOptions }) => (
  <div className="ch-docs ch-faq" id="xp-faq">
    <h2>How are people ranked?</h2>
    <div className="xp-live">
      <p>
        <strong>Current formula</strong>{" "}
        <span className="xp-note">(changes as you check the boxes above)</span>
      </p>
      <LiveFormula options={options} />
    </div>
    <ul>
      <li>
        <strong>Papers:</strong> up to 1,000 papers retrieved by a Paperclip search of titles and
        abstracts. A preprint and its published version count as one paper.
      </li>
      <li className={options.foundational ? undefined : "xp-off"}>
        <strong>
          <T k="found">Foundational papers</T>
        </strong>
        <Opt on={options.foundational} />
        Keyword search under-retrieves older landmark papers. This adds the 150 papers most cited
        by the on-topic papers (each cited by at least 3 of them), so that the founders of a field
        are not ranked below later contributors.
      </li>
      <li>
        <strong>
          <T k="topic">{TOPIC}:</T>
        </strong>{" "}
        the probability, from 0 to 1, that the topic is a main subject of the paper. A language
        model first writes a definition of the topic that names related but out-of-scope topics
        (&ldquo;Topic read as&rdquo;, above the list after a search); GXL&rsquo;s model then
        classifies each title and abstract against it. Each out-of-scope topic, unless it is a
        broader field (&ldquo;general &hellip;&rdquo;), is also checked on its own: the model
        chooses whether the paper is mainly about it or the topic, and {TOPIC} is multiplied by
        1 &minus; P(out-of-scope topic). Papers with {TOPIC} below 0.4 get weight 0.
      </li>
      <li className={scoreOnly(options)}>
        <strong>
          <T k="review">{REVIEW}</T>
        </strong>{" "}
        (review factor): 0.3 for a review, 1 for primary research. P(review) is the probability
        that the paper reviews other studies rather than reporting its own data or analysis.
        Papers without an abstract are not classified as reviews.
      </li>
      <li className={scoreOnly(options)}>
        <strong>
          <T k="cites">{CITES}</T>
        </strong>{" "}
        (log citations): citations are the papers citing it in GXL&rsquo;s citation graph. 1 at 0
        citations, 11.5 at 10, 31.5 at 100, 62.5 at 1,000. The logarithm keeps one very highly
        cited paper from outweighing a body of work; squaring it lets a highly cited paper clearly
        outweigh a few little-cited ones.
      </li>
      <li className={options.recent ? scoreOnly(options) : "xp-off"}>
        <strong>
          <T k="age">{AGE}</T>
        </strong>{" "}
        (recency decay, Recent work)
        <Opt on={options.recent} />a half-life of 3 years: 1 for this year, 0.5 at 3 years, 0.25
        at 6. It favors researchers active now; with first authors only, emerging researchers.
      </li>
      <li className={scoreOnly(options)}>
        <strong>
          <T k="share">Authorship credit:</T>
        </strong>{" "}
        first, last and sole authors receive the full paper weight; each of m middle authors
        receives 0.5 ÷ √m of it (0.5 for one middle author, 0.25 each for 4, 0.17 each for 9).
      </li>
      <li className={options.first_authors ? undefined : "xp-off"}>
        <strong>First authors only</strong>
        <Opt on={options.first_authors} />
        Only first and sole authors, typically the researchers who carried out the work, receive
        credit; senior (last) authors receive none.
      </li>
      <li>
        <strong>Rank by</strong> <span className="xp-opt">{RANK_BY_LABELS[options.rank_by]}</span>
        GXL score sums each person&rsquo;s paper weights, as described here. The other choices
        are plain counts over each
        person&rsquo;s on-topic papers, with no weighting by topic fit, citations or author
        position: the number of papers, their total citations, or their h-index (h papers cited
        at least h times each). The model still decides which papers are on topic ({TOPIC} at
        least 0.4). First authors only counts only the papers a person is first or sole author
        of; Recent work counts only papers from the last 3 years. Total citations favor one
        heavily cited paper, including for its middle authors; paper counts favor prolific
        authors; the h-index needs both.
      </li>
    </ul>
  </div>
);
